
import { defineComponent } from '@nuxtjs/composition-api';
import type { PropOptions } from 'vue';
import { mapMutations, mapState } from 'vuex';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import BravadoSpinnerV2 from '@/components/common/BravadoSpinnerV2/BravadoSpinnerV2.vue';
import ImageUploader from '@/components/common/ImageUploader.vue';
import PortfolioButton from '@/components/partials/public-portfolio/PortfolioButton/PortfolioButton.vue';
import PublicPortfolioConnectionActions from '@/components/partials/public-portfolio/PublicPortfolioConnectionActions/PublicPortfolioConnectionActions.vue';
import PublicPortfolioConnectionsImportModal from '@/components/partials/public-portfolio/PublicPortfolioConnections/PublicPortfolioConnectionsImportModal.vue';
import PublicPortfolioConnectionsSuccessModal from '@/components/partials/public-portfolio/PublicPortfolioConnections/PublicPortfolioConnectionsSuccessModal.vue';
import PublicPortfolioConnectModal from '@/components/partials/public-portfolio/PublicPortfolioConnections/PublicPortfolioConnectModal.vue';
import PublicPortfolioHeroConnections from '@/components/partials/public-portfolio/PublicPortfolioHeroConnections/PublicPortfolioHeroConnections.vue';
import PublicPortfolioHeroMenu from '@/components/partials/public-portfolio/PublicPortfolioHeroMenu/PublicPortfolioHeroMenu.vue';
import PublicPortfolioHeroContacts from '@/components/partials/public-portfolio/PublicPortfolioHero/PublicPortfolioHeroContacts.vue';
import PublicPortfolioSectionMixin from '@/components/partials/public-portfolio/mixins/publicPortfolioSectionMixin';
import PublicPortfolioHeroDataMixin from '@/components/partials/public-portfolio/PublicPortfolioHero/mixins/publicPortfolioHeroDataMixin';
import { EDIT_SECTION_EVENT_NAME } from '@/components/partials/public-portfolio/constants';
import UploadPublicPortfolioCurrentUserMutation from '@/apollo/mutations/Portfolio/UpdatePublicPortfolioCurrentUser.mutation';
import uploader from '@/components/utils/uploader';
import type { FormState } from '@/types/common';
import type { Mutation } from '@/types/graphql';
import type { SectionMode } from '@/components/partials/public-portfolio/types';

export default defineComponent({
  name: 'PublicPortfolioHero',
  components: {
    BravadoButtonV3,
    BravadoSpinnerV2,
    ImageUploader,
    PortfolioButton,
    PublicPortfolioConnectionActions,
    PublicPortfolioConnectionsImportModal,
    PublicPortfolioConnectionsSuccessModal,
    PublicPortfolioConnectModal,
    PublicPortfolioHeroConnections,
    PublicPortfolioHeroMenu,
    PublicPortfolioHeroContacts,
  },
  mixins: [PublicPortfolioSectionMixin, PublicPortfolioHeroDataMixin],
  props: {
    mode: {
      type: String,
      default: 'view',
    } as PropOptions<SectionMode>,
  },
  data(): { maxFileSizeMb: number; uploadState: FormState; isImageFailed: boolean } {
    return {
      maxFileSizeMb: 5,
      uploadState: 'idle',
      isImageFailed: false,
    };
  },
  computed: {
    ...mapState('portfolio', ['user', 'isPersonal', 'contacts', 'linkedInImportState']),
    positionAtCompany(): string {
      return `${this.user.position ?? ''}${this.user.current_company ? ` at ${this.user.current_company}` : ''}${
        (this.user?.war_room_years_of_experience ?? 0) > 0 && (this.user.position || this.user.current_company)
          ? ','
          : ''
      }`;
    },
    showAvatar(): boolean {
      return (
        this.linkedInImportState === 'idle' &&
        this.uploadState === 'idle' &&
        !!this.user.avatar_url &&
        !this.isImageFailed
      );
    },
    avatarClass(): Record<string, boolean> {
      return {
        'public-portfolio-hero__avatar': true,
        'public-portfolio-hero__avatar--empty': this.isEditMode && this.isEmptyAvatar,
        'public-portfolio-hero__avatar--placeholder': !this.isEditMode && this.isEmptyAvatar,
      };
    },
    isEditMode(): boolean {
      return this.mode === 'edit';
    },
    isEmptyAvatar(): boolean {
      return !this.user.avatar_url || this.uploadState !== 'idle';
    },
    avatarSize(): number {
      return this.$device.isMobile ? 130 : 180;
    },
  },
  methods: {
    ...mapMutations('portfolio', ['updateUser']),
    async uploadAvatar(_: string, file: File | null = null): Promise<void> {
      this.uploadState = 'submitting';

      if (file) {
        const { signed_id } = await uploader.upload({ file, token: this.$auth.user!.token! });

        try {
          await this.$apollo.mutate<Mutation['updatePublicPortfolioCurrentUser']>({
            mutation: UploadPublicPortfolioCurrentUserMutation,
            variables: {
              input: {
                avatarSignedBlobId: signed_id,
              },
            },
            // @ts-ignore
            update: (store, { data: { updatePublicPortfolioCurrentUser } }) => {
              this.updateUser({ avatar_url: updatePublicPortfolioCurrentUser.avatarUrl });
            },
          });
          this.$notice.success('Avatar updated!');
          this.uploadState = 'idle';
        } catch (e) {
          this.uploadState = 'idle';
          this.$notice.error(e);
          this.$sentryUtils.error(e);
        }
      }
    },
    onAvatarSizeError() {
      this.uploadState = 'idle';
      this.$notice.error(`File should be less than ${this.maxFileSizeMb}mb!`);
    },
    onEditAvatar() {
      this.$analytics.logEvent(EDIT_SECTION_EVENT_NAME, {
        name: 'avatar',
      });
      // @ts-ignore $refs' does not exist on type 'Vue | Element'
      this.$refs.imageUploader.$refs.file.click();
    },
    onContactButtonClick() {
      if (!this.isPersonal) {
        this.$analytics.logEvent('public-portfolio__contact-button__click', {
          user: 'non-member',
        });
      }
    },
    onPersonalInfoButtonClick(buttonKey: string): () => void {
      return () => {
        this.$analytics.logEvent(EDIT_SECTION_EVENT_NAME, {
          name: 'personal-info',
          button: buttonKey,
        });

        this.$router.push({
          query: { dynamic_modal: this.$constants.dynamicModals.PORTFOLIO_EDIT_PERSONAL_INFO_MODAL },
        });
      };
    },
    handleError() {
      this.isImageFailed = true;
    },
  },
});
